import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Card from "olive-react-myoneui/lib/Card";
import Row from "olive-react-myoneui/lib/Row";
import Col from "olive-react-myoneui/lib/Col";
import { useNavigate } from 'react-router-dom';
import BreadCrumbs from '../BreadCrumbs';
import IntroCardBundle from '../card/IntroCardBundle';
import CardEvent from "../events/CardEvent";
import CourseItem from '../card/CourseItem';
import { liveIcon, coursesIcon, homeIcon, leftArrowLong } from "../../../constants/icons";
import RecentEventSlot from '../events/RecentEventSlot';
import { getBundleDetail } from '../../../../api/afterLogin/bundles';
import { getEnrolledEvents } from '../../../../api/afterLogin/events';
import { addDays, format, parseISO } from "date-fns";
import { imageSrc, courseMaterialUrl, filterUrl } from '../../../../helpers/url_helper';
import { sanitizeSlug } from '../../../../helpers/string_helper';
import { CourseItemLoader, IntroCardLoader, UpcomingEventLoader } from '../../Loader';
import Error404 from '../../../../modules/Error404';
import Error403 from '../../../../modules/Error403';
import { checkDisabledSp } from '../../../../helpers/permission_helper';

const CourseBundleDetails = ({ className }) => {

    const [isExpanded, setIsExpanded] = useState(false);
    const adminData  = useSelector((state) => state.admin);
    const configData = useSelector((state) => state.config);
    const learnerData = useSelector((state) => state.user.user);
    const paths = useSelector((state) => state.user.paths);
    const [bundleDetail, setBundleDetail] = useState([]);
    const [bundleCourses, setBundleCourses] = useState([]);
    const [bundleEvents, setBundleEvents] = useState([]);
    const [bundleTrainingCourses, setBundleTrainingCourses] = useState([]);
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEventLoading, setIsEventLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isRatingChanged, setIsRatingChanged] = useState(false);
    const { slug } = useParams();
    const navigate = useNavigate();

    /* ACCESS CHECK */
    const products  = adminData.products;
    const userSpEnabled = adminData?.user_sp_enabled;
    const [noAccess, setNoAccess] = useState(checkDisabledSp('mykademy', products, userSpEnabled));

    const coursePages = [
        { icon: homeIcon, label: "Home", url: "/student-dashboard/" },
        { icon: coursesIcon, label: "Bundles", url: "/student-dashboard/bundles" },
        { label: bundleDetail?.c_title ?? 'Bundle' }
    ];

    const fetchBundleDetail = async (params) => {
        if (params.slug == '') {
            setHasError(true);
            return;
        }
        try {
            const response = await getBundleDetail(params);
            setIsLoading(false);
            if (response && response.data) {
                setBundleDetail(response.data);
                setBundleCourses(response.data.bundles.courses)
                setBundleEvents(response.data.bundles.training_events)
                setBundleTrainingCourses(response.data.bundles.training_courses)
            } else {
                setHasError(true);
            }
        } catch (error) {
            setIsLoading(false);
            setHasError(true);
            console.error(error);
        }
    };

    const fetchUpcomingEvents = async () => {
        try {
            const response = await getEnrolledEvents(configData.access_token, '', { "enroll_status": ["not-started"], "item_type":"all" });
            if (response && response.data) {
                setUpcomingEvents(response.data);
            }
            setIsEventLoading(false)
        } catch (error) {
            setIsEventLoading(false)
            console.error(error);
        }
    };


    const toggleDescription = () => {
        setIsExpanded(!isExpanded);
      };
    
    const handleBackClick = () => {
        navigate('/bundles');
    };

    const asRatingUpdate = () => {
        setIsRatingChanged((prev) => !prev);
    }

    const getEnrolledEventStatus = (startDateTime, endDateTime, activities) => {
      if (new Date(startDateTime) > new Date()) {
        return 'Not Started';
      } else if (new Date(endDateTime) >= new Date()) {
        return 'Ongoing';
      } else if (new Date(endDateTime) < new Date()) {
        let eventStatus = 0, courseStatus = 0, bundleStatus = 0;
        if (activities.length > 0) {
          activities.forEach((activity) => {
            if(activity.ea_activity_type === 'session') {
              eventStatus = activity.ll_attended === 1 ? 100 : activity.ea_session_delivery_type === 'offline'  ? 100 : 0;
            } else if(activity.ea_activity_type === 'Course') {
              courseStatus = activity.cs_percentage !== null && activity.cs_percentage === 100 ? 100 : 0;
            } else if(activity.ea_activity_type === 'Bundle') {
              bundleStatus = activity.bs_percentage !== null && activity.bs_percentage === 100 ? 100 : 0;
            }
          });
          const totalStatus = (eventStatus + courseStatus + bundleStatus) / activities.length;
          if (totalStatus === 100) {
            return 'Completed';
          } else {
            return 'Incomplete';
          }
        } else {
          return 'Incomplete';
        }
      }
    }

    const calculateAverageRating = (data) => {
        if (!data) return 0;
        const totalReviews = data ? data.length : 0;
        const totalRating  = data ? data.reduce((acc, item) => acc + item.rating, 0) : 0;
        return totalReviews > 0 ? totalRating : 0;
    };

    const bundleCourseName = bundleDetail?.c_title ?? '';
    const courseData = bundleCourses && bundleCourses?.filter(course => course !== null && course !== undefined && course !== '')?.map((course) => {
        const isExpired = bundleDetail?.c_access_validity === '2' ? new Date(bundleDetail?.c_validity_date) > new Date() : 
                        bundleDetail?.c_access_validity === '1' ? addDays(new Date(bundleDetail?.updated_date ?? bundleDetail?.created_date ?? ''), parseInt(bundleDetail?.c_validity)) > new Date() : false
        
    const deadlineDate = bundleDetail?.c_access_validity === '0' ? 'Lifetime Validity' : 
                        course?.cs_course_validity_status === '0' ? 'Lifetime Validity' :
                        course?.cs_end_date ? 'Deadline : ' + format(new Date(course?.cs_end_date), 'MMM d, yyyy') : 'Lifetime Validity';         
        
        return {
            className: isExpired ? 'expired' : '',
            courseImage: imageSrc('course', course?.cb_image, paths),
            courseTitle: course?.cb_title,
            courseDeadline: deadlineDate,
            courseLectureCount: course?.lecture_count,
            courseTypeLearning: course?.category,
            courseStartButton: "Start Course",
            courseStartLink: course?.cs_course_id ? courseMaterialUrl(course.cs_course_id,0,`student-dashboard/bundles/${slug}?bundle_id=${bundleDetail?.bs_bundle_id}`, false) : '',
            courseResumeLink: course?.cs_last_played_lecture !== null && course?.cs_course_id ? courseMaterialUrl(course.cs_course_id, course.cs_last_played_lecture,`student-dashboard/bundles/${slug}?bundle_id=${bundleDetail?.bs_bundle_id}`, false) : '',
            courseDetailLink: `/student-dashboard/courses/detail/${course?.cb_slug}` ?? '',
            courseButtonLabel: "Start Course",
            courseProgressPercentage: course?.cs_percentage ?? 0,
            courseStatus: course?.enroll_status === 'not-started' ? 'notStarted' :
                          course?.enroll_status === 'inprogress'  ? 'inProgress' : 'completed'
        }
    })

    const eventData = bundleEvents && bundleEvents?.filter(event => event !== null && event !== undefined && event !== '').map((event) => {
        const startDateTime = event?.bce_start_date ? event?.bce_start_date.split('T')[0] + 'T' + event?.bce_start_time + 'Z' : new Date();
        const endDateTime = event?.bce_end_date ? event?.bce_end_date.split('T')[0] + 'T' + event?.bce_end_time + 'Z' : new Date();
        const isExpired   = new Date(parseISO(endDateTime)) < new Date() ? true : false;
        let map = {
            className: event?.bce_end_date && isExpired ? 'expired' : '',
            eventId: event?.bce_id ?? '',
            eventImage: imageSrc('event', event?.bce_thumbnail, paths),
            eventType: event?.bce_type ? event.bce_type.charAt(0).toUpperCase() + event.bce_type.slice(1) : '',
            eventIcon: liveIcon,
            eventTitle: event?.bce_name ?? '',
            eventDeadline: event?.bce_status === '0' || event?.bes_status === '3' ? 'Cancelled' : event?.bce_end_date ? "Deadline: " + format(new Date(event?.bce_end_date), 'dd/MM/yyyy') : '',
            eventLink: event?.bce_slug ? `/student-dashboard/events/${event.bce_slug}` : '',
            eventCancelledStatus: event?.bce_status === '0' || event?.bes_status === '3' ? true : false,
            eventStatus: getEnrolledEventStatus(startDateTime, endDateTime, event?.event_activities)
        }

        if (event?.bce_delivered_type === 'venue') {
            map.eventButton    = event?.bce_status === '0' || event?.bes_status === '3' ? "Event Cancelled" : "View Event details"
            map.eventStartLink = event?.bce_status === '0' || event?.bes_status === '3' ? false : `/student-dashboard/events/${event.bce_slug}`
        } else if (event?.bce_delivered_type === 'live') {
            map.eventButton    = event?.bce_status === '0' || event?.bes_status === '3' ? "Event Cancelled" : isExpired ? "Event Expired" : "Join now"
            map.eventStartLink = event?.bce_status === '0' || event?.bes_status === '3' ? false : isExpired ? false : event.event_activities[0].live_classes.lc_session_link+'?user_token='+configData.access_token+'&auto_redirect=true'
            map.linkToCopy     = event?.bce_status === '0' || event?.bes_status === '3' ? false : isExpired ? false : event.event_activities[0].live_classes.lc_session_link+'?user_token='+configData.access_token+'&auto_redirect=true'
        }
        return map;
    })

    const trainingCourseData = bundleTrainingCourses && bundleTrainingCourses?.filter(trainingCourse => trainingCourse !== null && trainingCourse !== undefined && trainingCourse !== '').map((trainingCourse) => {
        const endDateTime = trainingCourse?.bce_end_date ? trainingCourse?.bce_end_date.split('T')[0] + 'T' + trainingCourse?.bce_end_time + 'Z' : new Date(); 
        const isExpired   = new Date(parseISO(endDateTime)) < new Date() ? true : false;
        return {
            className: trainingCourse?.bce_end_date && isExpired ? 'expired' : '',
            eventId: trainingCourse?.bce_id ?? '',
            eventImage: imageSrc('event', trainingCourse?.bce_thumbnail, paths),
            eventType: trainingCourse?.bce_delivered_type === 'venue' ? 'Offline' : 'Online',
            eventIcon: liveIcon,
            eventTitle: trainingCourse?.bce_name ?? '',
            eventDeadline: trainingCourse?.bce_status === '0' || trainingCourse?.bes_status === '3' ? 'Cancelled' : trainingCourse?.bce_end_date ? "Deadline: " + format(new Date(trainingCourse?.bce_end_date), 'dd/MM/yyyy') : '',
            eventButton: trainingCourse?.bce_status === '0' || trainingCourse?.bes_status === '3' ? "Event Cancelled" : "View Event details",
            eventStartLink: trainingCourse?.bce_status === '0' || trainingCourse?.bes_status === '3' ? false : trainingCourse?.bce_slug ? `/student-dashboard/events/${trainingCourse?.bce_slug}` : '',
            eventCancelledStatus: trainingCourse?.bce_status === '0' || trainingCourse?.bes_status === '3' ? true : false
        }
    })

    const courseOverviewData = {
        courseDescDetails: {
          title: bundleDetail?.c_title ?? '',
          description: bundleDetail?.c_description ?? '',
          backIcon: leftArrowLong,
        }
      }; 
    
    const upcomingEventData = upcomingEvents && upcomingEvents?.filter((event) => event.bce_delivered_type === 'live').map((event) => {
        const startTime = `${event?.event_activities[0].ea_session_start_date}T${event?.event_activities[0].ea_session_start_time}Z`;
        const endTime   = `${event?.event_activities[0].ea_session_end_date}T${event?.event_activities[0].ea_session_end_time}Z`;
        return {
            eventHeading: event?.event_activities[0]?.ea_title,
            eventDetailLink: event.bce_slug ? `/student-dashboard/events/${event.bce_slug}` : '',
            eventLink: event?.event_activities[0].live_classes.lc_session_link+'?user_token='+configData.access_token+'&auto_redirect=true',
            eventBtnLabel: "Join",
            paraDescTime: format(parseISO(startTime), "d MMMM yyyy h:mm a") + ' - ' + format(parseISO(endTime), "d MMMM yyyy h:mm a"),
            paraDesc: event?.event_activities[0].ea_session_delivery_type,
            eventDay: format(parseISO(startTime), "d"),
            eventMonth: format(parseISO(startTime), "MMM"),
        }
    })
    
    const maxChars = 500;
    const displayDesc = isExpanded
        ? courseOverviewData.courseDescDetails.description
        : courseOverviewData.courseDescDetails.description !== '' && 
          courseOverviewData.courseDescDetails.description.length > maxChars 
          ? `${courseOverviewData.courseDescDetails.description.substring(0, maxChars)}...` 
          : courseOverviewData.courseDescDetails.description;

    useEffect(() => {
        fetchBundleDetail({accessToken: configData.access_token, slug: sanitizeSlug(slug)})
        fetchUpcomingEvents()
    },[slug, isRatingChanged])
    
    return (
        <>
        {noAccess ? <Error403 /> :
         hasError ? (<Error404 className="errow-with-nav"/>) : (
        <>
            <BreadCrumbs pages={coursePages} />
            <div className="myk__stu-guide-course-title">
                <span onClick={handleBackClick}>{leftArrowLong}</span>
                <h2>{bundleCourseName}</h2>
            </div>
            <div className={`${className || ""} myk__stu-courses-wrapper myk__stu-courses_bundle-wrapper`}>
                <Row>
                    <Col md={{ span: 12, order: 0 }} lg={{ span: 12, order: 0 }} xl={{ span: 8, order: 0 }} className="myk__course-details-col">
                        {isLoading === false ? (
                        <IntroCardBundle
                            id={bundleDetail?.c_id ?? ''}
                            imgSrc={imageSrc('bundle',bundleDetail?.c_image,paths)}
                            deadlineText={bundleDetail?.c_access_validity === '0' ? 'Lifetime Validity' : 
                                bundleDetail?.bs_end_date ? 'Deadline : ' + format(new Date(bundleDetail?.bs_end_date), 'MMM d, yyyy') : 'Lifetime Validity'}
                            subTitle={bundleDetail?.c_title ?? 'Bundle'}
                            initialRating="3"
                            coursesCount={courseData?.length ?? 0}
                            eventsCount={eventData?.length ?? 0}
                            trainingCoursesCount={trainingCourseData?.length ?? 0}
                            showRating={bundleDetail?.c_rating_enabled === '1' ? true : false}
                            asRatingUpdate={asRatingUpdate}
                            overallRating={bundleDetail?.overall_rating ? calculateAverageRating(bundleDetail?.overall_rating) : 0}
                            isSelfRated={bundleDetail?.bundle_reviews?.self_review !== null ? true : false}
                        />
                        ) : (
                        <IntroCardLoader />
                        )}
                        <Card className="border-0 mt-5 p-3 py-3">
                            <Card.Body className="p-0">
                                {bundleDetail && courseData && courseData.length > 0 && isLoading === false ? (
                                <Row>
                                    <div className="myk_stu-page-header mb-3">
                                        <h2>Courses <span className="myk__stu-bundle-count">{courseData.length}</span></h2>
                                    </div>
                                    <Col>
                                        <div className="myk__stu-bundle_course-list mb-4">
                                            {courseData.map((course, index) => (
                                                <CourseItem key={index} {...course} bundleId={bundleDetail?.bs_bundle_id ?? ''} />
                                            ))}
                                        </div>
                                    </Col>
                                </Row>
                                ) : (isLoading) ? (
                                <Row>
                                    <Col>
                                        <CourseItemLoader type={'course'} items={8} />
                                    </Col>
                                </Row>
                                ) : (<></>)}
                                {bundleDetail && eventData && eventData.length > 0 && isLoading === false ? (
                                <Row>
                                    <div className="myk_stu-page-header mb-3">
                                        <h2>Training Events <span className="myk__stu-bundle-count">{eventData.length}</span></h2>
                                    </div>
                                    <Col>
                                        <div className="myk__stu-bundle_course-list mb-4">
                                            {eventData.map((event, index) => (
                                                <CardEvent key={index} {...event} />
                                            ))}
                                        </div>
                                    </Col>
                                </Row>
                                ) : (isLoading) ? (
                                <Row>
                                    <Col>
                                        <CourseItemLoader type={'course'} items={4} />
                                    </Col>
                                </Row>
                                ) : (<></>)}
                                {bundleDetail && trainingCourseData && trainingCourseData.length > 0 && isLoading === false &&
                                <Row>
                                    <div className="myk_stu-page-header mb-3">
                                        <h2>Training Courses <span className="myk__stu-bundle-count">{trainingCourseData.length}</span></h2>
                                    </div>
                                    <Col>
                                        <div className="myk__stu-bundle_course-list mb-4">
                                            {trainingCourseData.map((event, index) => (
                                                <CardEvent key={index} {...event} />
                                            ))}
                                        </div>
                                    </Col>
                                </Row>
                                 }
                            </Card.Body>
                        </Card>
                        <Card className="border-0 mt-4 p-4">
                            <Card.Body className="p-0">
                                <div className="myk__stu-bundle_course-about">
                                    <h2>About the Bundle Course</h2>
                                    <div dangerouslySetInnerHTML={{ __html: displayDesc }} />
                                    {courseOverviewData.courseDescDetails.description !== '' && courseOverviewData.courseDescDetails.description > maxChars ? 
                                    (<span className="btn btn-transparent mt-0 p-0" onClick={toggleDescription}>{isExpanded ? "Show Less" : "Read More"}</span>) :
                                    ''}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={{ span: 12, order: 0 }} lg={{ span: 12, order: 0 }} xl={{ span: 4, order: 0 }} className="myk__course-details-col">
                        <Card className="border-0 mt-0 p-4 h-100">
                            <Card.Body className="p-0">
                                <div className="myk__stu-bundle_course-about">
                                    <h2>Upcoming Activities </h2>
                                    {isEventLoading === false ? (
                                    <div className="myk_stu-bundle_activites-wrapper">
                                        {upcomingEventData && upcomingEventData.length > 0 ?upcomingEventData?.map((event, index) => (
                                            <RecentEventSlot
                                                key={index}
                                                eventHeading={event?.eventHeading}
                                                eventBtnLabel={event?.eventBtnLabel}
                                                eventDetailLink={event?.eventDetailLink}
                                                eventLink={event?.eventLink}
                                                paraDescTime={event?.paraDescTime}
                                                paraDesc={event?.paraDesc}
                                                eventDay={event?.eventDay}
                                                eventMonth={event?.eventMonth}
                                                className="mb-3"
                                            />
                                        )) : ('No Upcoming Events')}
                                    </div>
                                    ) : (
                                    <UpcomingEventLoader items={2} />
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
        )}
        </>
    );
}

export default CourseBundleDetails;
